import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons/faMagnifyingGlass";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { Breakpoint, IconButton, InputAdornment, SxProps, Theme } from "@mui/material";
import { useDebouncedCallback } from "@react-hookz/web";
import {
  KeyboardEvent,
  FocusEvent,
  forwardRef,
  ChangeEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DEFAULT_DEBOUNCE_TIME } from "../../../constants/elements";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";
import { CircularProgress } from "../../Feedback/CircularProgress/CircularProgress";
import { FormControl } from "../FormControl/FormControl";
import { TextInput } from "../TextInput/TextInput";

export type SearchFieldProps = {
  id: string;
  name?: string;
  onBlur?(event: FocusEvent<HTMLInputElement>): void;
  onChange(value: string): void;
  onKeyDown?(event: KeyboardEvent<HTMLInputElement>): void;
  placeholder?: string;
  initialValue?: string;
  debounceTime?: number;
  loading?: boolean;
  /** Removes the border and background colour from the input, starting at the specified Breakpoint upwards */
  unfilledFrom?: Breakpoint;
  sx?: SxProps<Theme>;
  inputProps?: Record<string, unknown>;
};

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    {
      id,
      inputProps,
      name,
      placeholder,
      initialValue,
      unfilledFrom,
      loading,
      onBlur,
      onChange,
      onKeyDown,
      debounceTime = DEFAULT_DEBOUNCE_TIME,
      sx,
    },
    ref,
  ): ReactElement => {
    const [value, setValue] = useState(initialValue || "");

    const debounceChange = useDebouncedCallback(onChange, [onChange], debounceTime);

    const handleChange = useCallback(
      ({ target }: ChangeEvent<HTMLInputElement>) => {
        setValue(target?.value);
        if (debounceTime) {
          debounceChange(target?.value);
        } else {
          onChange(target?.value);
        }
      },
      [debounceChange, debounceTime, onChange],
    );

    const onClear = useCallback(() => {
      setValue("");
      onChange("");
    }, [onChange]);

    useEffect(() => {
      setValue(initialValue || "");
    }, [initialValue]);

    const sxProps: SxProps | undefined = useMemo(() => {
      if (!unfilledFrom) {
        return undefined;
      }
      const bp = unfilledFrom;
      return {
        background: { [bp]: "none" },
        boxShadow: { [bp]: 0 },
        "& fieldset": {
          border: { [bp]: 0 },
        },
        "& input": {
          background: { [bp]: "none" },
        },
      };
    }, [unfilledFrom]);

    return (
      <FormControl id={id} fullWidth>
        <TextInput
          inputRef={ref}
          name={name}
          data-testid={`SearchField-Input-${id}`}
          placeholder={placeholder}
          value={value}
          sx={[sxProps, ...(Array.isArray(sx) ? sx : [sx])]}
          onBlur={onBlur}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          inputProps={{
            "aria-busy": loading,
            ...inputProps,
          }}
          startAdornment={
            <InputAdornment position="start">
              {loading ? <CircularProgress size={15} /> : <FaIcon icon={faMagnifyingGlass} />}
            </InputAdornment>
          }
          endAdornment={
            value ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear"
                  data-testid={`SearchField-ClearButton-${id}`}
                  onClick={onClear}
                >
                  <FaIcon icon={faXmark} />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }
        />
      </FormControl>
    );
  },
);
